import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment, Chip, Collapse, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SubCategoryModal from './SubCategoryModal';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';

function SubCategoryIndex() {
  useSessionValidation();

  const [data, setData] = useState([]);                        // all sub-categories
  const [categories, setCategories] = useState([]);            // all parent categories
  const [expandedCategories, setExpandedCategories] = useState({}); // { [catId]: bool }

  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);

  const { isAuthenticated, companyId, logout, token } = useAuth();
  const { logAudit } = useAuditLog();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Active category filter tab
  const [activeCategoryFilter, setActiveCategoryFilter] = useState('all');

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return;
    setLoading(true);
    Promise.all([fetchSubCategories(), fetchCategories()]).finally(() => setLoading(false));
  }, [companyId, token]);

  const fetchSubCategories = async () => {
    try {
      const response = await fetchData('SubCategoriesApi', `/${companyId}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching sub-categories:', error);
      setData([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetchData('CategoryApi', `/${companyId}`, token);
      if (response && response.response_data) {
        setCategories(response.response_data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    await Promise.all([fetchSubCategories(), fetchCategories()]);
    setLoading(false);
  };

  const handleDelete = async () => {
    deleteData('SubCategoriesApi', `${deleteItemId}`, token)
      .then(() => {
        logAudit(AUDIT_ACTION.DELETE, 'SubCategoriesApi', deleteItemId, { Id: deleteItemId }, null);
        refreshData();
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
      })
      .catch((error) => console.error('Error deleting record:', error));
  };

  const handleActivate = (itemId) => {
    activateData('SubCategoriesApi', `${itemId}`, token)
      .then(() => refreshData())
      .catch((error) => console.error('Error activating record:', error));
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  const handleOpenModal = (mode, subCategory = null) => {
    setModalMode(mode);
    setSelectedSubCategory(subCategory);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSubCategory(null);
  };

  const handleSave = () => refreshData();

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Toggle expand/collapse for a category group
  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  // Expand all by default once data loads
  useEffect(() => {
    if (categories.length > 0) {
      const allExpanded = {};
      categories.forEach(c => { allExpanded[c.Id] = true; });
      setExpandedCategories(allExpanded);
    }
  }, [categories]);

  // Get category name helper
  const getCategoryName = (catId) => {
    const cat = categories.find(c => String(c.Id) === String(catId));
    return cat ? cat.Name : `Category ${catId}`;
  };

  // Filter sub-categories
  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = item.Name?.toLowerCase().includes(searchLower) ||
      getCategoryName(item.CategoryId).toLowerCase().includes(searchLower);
    const matchesFilter = activeCategoryFilter === 'all' ||
      String(item.CategoryId) === String(activeCategoryFilter);
    return matchesSearch && matchesFilter;
  });

  // Group filtered data by CategoryId
  const groupedData = filteredData.reduce((acc, item) => {
    const key = item.CategoryId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  // Flatten for pagination (list mode)
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  // Categories that actually have sub-categories
  const usedCategoryIds = [...new Set(data.map(d => d.CategoryId))];
  const usedCategories = categories.filter(c => usedCategoryIds.includes(c.Id));

  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Tooltip title="View Details" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('view', item)}
          style={{
            minWidth: '22px', height: '22px', borderRadius: '6px',
            color: 'white', padding: '2px', border: 'none',
            fontSize: '10px', fontWeight: '600',
            boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)'
          }}
        >👁️</Button>
      </Tooltip>

      <Tooltip title="Edit Sub-Category" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('edit', item)}
          style={{
            minWidth: '22px', height: '22px', borderRadius: '6px',
            color: 'white', padding: '2px', border: 'none',
            fontSize: '10px', fontWeight: '600',
            boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)'
          }}
        >✏️</Button>
      </Tooltip>

      <Tooltip title={item.IsActive ? 'Delete Sub-Category' : 'Activate Sub-Category'} arrow>
        <Button
          size="small"
          onClick={() => item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id)}
          style={{
            minWidth: '22px', height: '22px', borderRadius: '6px',
            color: 'white', padding: '2px', border: 'none',
            fontSize: '10px', fontWeight: '600',
            boxShadow: item.IsActive
              ? '0 1px 4px rgba(255, 107, 107, 0.3)'
              : '0 1px 4px rgba(78, 205, 196, 0.3)'
          }}
        >{item.IsActive ? '🗑️' : '✅'}</Button>
      </Tooltip>
    </div>
  );

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {loading && <LogoLoader open={loading} message="Loading sub-categories..." />}

        <div style={{
          maxWidth: '1400px', margin: '0 auto', background: 'white',
          borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          padding: '12px', position: 'relative', color: 'black',
          fontFamily: 'Times New Romans'
        }}>

          {/* ── Header ── */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0'
          }}>
            <h4 style={{
              fontSize: '17px', fontWeight: '700', margin: 0,
              letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase'
            }}>
              <span style={{ color: 'black' }}>Sub-Category</span>
            </h4>
          </div>

          {/* ── Search + Create ── */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by sub-category or category name..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#667eea', fontSize: '20px' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px', backgroundColor: '#f8f9fa',
                  fontSize: '13px', height: '29px',
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' }
                }
              }}
            />
            <Tooltip title="Create a new sub-category" arrow>
              <Button
                variant="contained"
                onClick={() => handleOpenModal('create')}
                style={{
                  background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                  color: 'white', padding: '4px 12px', borderRadius: '6px',
                  fontSize: '12px', fontWeight: '700',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                  border: 'none', minHeight: '29px', textTransform: 'none',
                  letterSpacing: '0.5px', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: '4px'
                }}
              >
                <span style={{ fontSize: '12px' }}>+</span>
                Create Sub-Category
              </Button>
            </Tooltip>
          </div>

          {/* ── Category Filter Chips ── */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
            <Chip
              label={`All (${data.length})`}
              size="small"
              onClick={() => { setActiveCategoryFilter('all'); setCurrentPage(1); }}
              style={{
                fontSize: '11px', height: '22px', cursor: 'pointer',
                background: activeCategoryFilter === 'all' ? '#2C3E6E' : '#e9ecef',
                color: activeCategoryFilter === 'all' ? 'white' : '#495057',
                fontWeight: activeCategoryFilter === 'all' ? '700' : '400'
              }}
            />
            {usedCategories.map(cat => {
              const count = data.filter(d => d.CategoryId === cat.Id).length;
              return (
                <Chip
                  key={cat.Id}
                  label={`${cat.Name} (${count})`}
                  size="small"
                  onClick={() => { setActiveCategoryFilter(cat.Id); setCurrentPage(1); }}
                  style={{
                    fontSize: '11px', height: '22px', cursor: 'pointer',
                    background: String(activeCategoryFilter) === String(cat.Id) ? '#2C3E6E' : '#e9ecef',
                    color: String(activeCategoryFilter) === String(cat.Id) ? 'white' : '#495057',
                    fontWeight: String(activeCategoryFilter) === String(cat.Id) ? '700' : '400'
                  }}
                />
              );
            })}
          </div>

          {/* ── Grouped View (when "All" selected and no search) ── */}
          {activeCategoryFilter === 'all' && !searchTerm ? (
            <div>
              {Object.keys(groupedData).length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '40px',
                  color: '#999', fontSize: '13px'
                }}>
                  No sub-categories found. Click "+ Create Sub-Category" to add one.
                </div>
              ) : (
                Object.keys(groupedData).map(catId => {
                  const catSubItems = groupedData[catId];
                  const catName = getCategoryName(catId);
                  const isExpanded = expandedCategories[catId] !== false; // default open

                  return (
                    <div key={catId} style={{ marginBottom: '10px' }}>
                      {/* Category Group Header */}
                      <div
                        onClick={() => toggleCategory(catId)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                          color: 'white', padding: '6px 12px',
                          borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
                          cursor: 'pointer', userSelect: 'none'
                        }}
                      >
                        {isExpanded
                          ? <KeyboardArrowDownIcon style={{ fontSize: '16px' }} />
                          : <KeyboardArrowRightIcon style={{ fontSize: '16px' }} />
                        }
                        <span style={{ fontWeight: '700', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {catName}
                        </span>
                        <Chip
                          label={catSubItems.length}
                          size="small"
                          style={{
                            height: '18px', fontSize: '10px', fontWeight: '700',
                            background: 'rgba(255,255,255,0.25)', color: 'white',
                            marginLeft: 'auto'
                          }}
                        />
                      </div>

                      {/* Sub-category rows */}
                      <Collapse in={isExpanded}>
                        <TableContainer component={Paper} style={{
                          boxShadow: 'none',
                          borderRadius: '0 0 8px 8px',
                          border: '1px solid #e9ecef',
                          borderTop: 'none',
                          overflow: 'hidden'
                        }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow style={{ background: '#f8f9fa', height: '24px' }}>
                                <TableCell style={{
                                  fontSize: '10px', fontWeight: '600', color: '#6c757d',
                                  textTransform: 'uppercase', letterSpacing: '0.4px',
                                  padding: '4px 12px', height: '24px'
                                }}>
                                  #
                                </TableCell>
                                <TableCell style={{
                                  fontSize: '10px', fontWeight: '600', color: '#6c757d',
                                  textTransform: 'uppercase', letterSpacing: '0.4px',
                                  padding: '4px 12px', height: '24px'
                                }}>
                                  Sub-Category Name
                                </TableCell>
                                <TableCell style={{
                                  fontSize: '10px', fontWeight: '600', color: '#6c757d',
                                  textTransform: 'uppercase', letterSpacing: '0.4px',
                                  padding: '4px 12px', height: '24px', textAlign: 'center'
                                }}>
                                  Status
                                </TableCell>
                                <TableCell style={{
                                  fontSize: '10px', fontWeight: '600', color: '#6c757d',
                                  textTransform: 'uppercase', letterSpacing: '0.4px',
                                  padding: '4px 12px', height: '24px', textAlign: 'right'
                                }}>
                                  Actions
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {catSubItems.map((item, idx) => (
                                <TableRow key={item.Id} style={{
                                  background: idx % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                  borderBottom: '1px solid #f0f0f0'
                                }}>
                                  <TableCell style={{ fontSize: '12px', padding: '6px 12px', color: '#adb5bd', width: '40px' }}>
                                    {idx + 1}
                                  </TableCell>
                                  <TableCell style={{ fontSize: '12px', padding: '6px 12px', color: '#2d3748', fontWeight: '500' }}>
                                    {item.Name}
                                  </TableCell>
                                  <TableCell style={{ fontSize: '12px', padding: '6px 12px', textAlign: 'center' }}>
                                    <Chip
                                      label={item.IsActive ? 'Active' : 'Inactive'}
                                      size="small"
                                      style={{
                                        height: '18px', fontSize: '10px', fontWeight: '600',
                                        background: item.IsActive ? '#d4edda' : '#f8d7da',
                                        color: item.IsActive ? '#155724' : '#721c24'
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell style={{ padding: '6px 12px', textAlign: 'right' }}>
                                    {renderActions(item)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Collapse>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            /* ── Flat Table (search / filter by single category) ── */
            <div>
              <TableContainer component={Paper} style={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                borderRadius: '10px', overflow: 'hidden', marginBottom: '10px'
              }}>
                <Table sx={{ minWidth: 500 }} aria-label="subcategory table">
                  <TableHead>
                    <TableRow style={{
                      background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                      height: '27px'
                    }}>
                      {['Category', 'Sub-Category Name', 'Status', 'Actions'].map((h, i) => (
                        <TableCell key={h} style={{
                          color: 'white', fontWeight: '600', fontSize: '11px',
                          textTransform: 'uppercase', letterSpacing: '0.5px',
                          padding: '4px 12px', height: '27px',
                          textAlign: i === 3 ? 'right' : i === 2 ? 'center' : 'left'
                        }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} style={{ textAlign: 'center', padding: '30px', color: '#999', fontSize: '13px' }}>
                          No sub-categories found matching your search.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedData.map((item, index) => (
                        <TableRow key={item.Id} style={{
                          background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          borderBottom: '2px solid #f0f0f0'
                        }}>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#667eea', fontWeight: '600' }}>
                            {getCategoryName(item.CategoryId)}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                            {item.Name}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'center' }}>
                            <Chip
                              label={item.IsActive ? 'Active' : 'Inactive'}
                              size="small"
                              style={{
                                height: '18px', fontSize: '10px', fontWeight: '600',
                                background: item.IsActive ? '#d4edda' : '#f8d7da',
                                color: item.IsActive ? '#155724' : '#721c24'
                              }}
                            />
                          </TableCell>
                          <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                            {renderActions(item)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination (flat view only) */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            </div>
          )}
        </div>

        {/* ── Delete Confirm Dialog ── */}
        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="delete-dialog-title"
          PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
        >
          <DialogTitle id="delete-dialog-title" style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>
            Delete Record?
          </DialogTitle>
          <DialogContent>
            <DialogContentText style={{ fontSize: '13px', color: '#666' }}>
              Are you sure you want to delete this sub-category record?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff', borderRadius: '6px', padding: '4px 8px',
                fontSize: '12px', fontWeight: '600', textTransform: 'none',
                border: 'none', display: 'flex', alignItems: 'center',
                gap: '4px', minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>❌</span> Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff', borderRadius: '6px', padding: '4px 8px',
                fontSize: '12px', fontWeight: '600', textTransform: 'none',
                border: 'none', display: 'flex', alignItems: 'center',
                gap: '4px', minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>✅</span> Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── SubCategory Modal ── */}
        <SubCategoryModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          subCategoryData={selectedSubCategory}
          mode={modalMode}
          existingSubCategories={data}
        />
      </div>
    </Fade>
  );
}

export default SubCategoryIndex;
